import React, { VFC, useState, useCallback } from 'react';
import { makeStyles, Button, ButtonProps } from '@dxb-web-ui-framework/design-system';
import { useNavigate } from 'react-router-dom';
import { TFunction } from 'react-i18next';
import { TransferServiceImpl } from '../../../../../fundTransfer/src/domain/Transfer/usecases';
import { ClientSideWizardFlowPresenter } from '../../../../../content-composer/src/components/ClientSideWizard/ClientSideWizardFlowPresenter';
import { DealerRatesResponseBody } from '../../../../../fundTransfer/src/domain/Transfer/entities/DealerRatesResponseDTO';
import { GenericDialog } from '../../../../../content-composer/src/components/Generic/GenericDialog/GenericDialog';
import { ConsolidatedTransactionDetails } from '../../../domain/Transaction/entities/ConsolidatedTransaction';
import { getRoutePath } from '../../../../../content-composer/src/components/Routes/routePaths';
import { useCorporateCustomerId, useSelectedCustomerId } from '../../../../../userProfile/src/contexts/UserInfo/hooks';
import { SuccessAcceptedDealerRate } from '../TransactionDetailsDealerRate';
import { InitiateRequestDealerInApp2FA } from '../TransactionDetailsDealerRate';

export interface AcceptAndFinalizeProps {
  isAcceptanceBtnDisabled: boolean;
  t: TFunction;
  dealerRates?: DealerRatesResponseBody;
  transactionDetails: ConsolidatedTransactionDetails;
  fundTransfersService: TransferServiceImpl;
  onRefetchList?: Function;
  onRefetchDetails: Function;
  externalButtonProps?: ButtonProps;
}

const useStyles = makeStyles(theme => ({
  dialogContentClassName: {
    paddingTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(4),
    },
  },
}));

const AcceptAndFinalize: VFC<AcceptAndFinalizeProps> = ({
  isAcceptanceBtnDisabled,
  t,
  transactionDetails,
  dealerRates,
  fundTransfersService,
  externalButtonProps,
  onRefetchDetails,
  onRefetchList,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<'initTransfer' | 'makeTransfer'>('initTransfer');
  const navigate = useNavigate();
  const companyId = useCorporateCustomerId();
  const selectedCustomerId = useSelectedCustomerId();

  const onToggleDealerRateAcceptanceDialog = useCallback(() => {
    if (currentStep === 'initTransfer') {
      setIsOpen(prev => !prev);
      return;
    }
    onRefetchDetails();
    setIsOpen(prev => !prev);
  }, [currentStep, onRefetchDetails]);

  const onNavigateBackToDashboard = useCallback(() => {
    onRefetchDetails();
    if (companyId || selectedCustomerId) {
      navigate(
        getRoutePath({
          routeKey: 'dashboard',
          customerId: (companyId as string) || (selectedCustomerId as string),
        }),
      );
    }
  }, [companyId, selectedCustomerId, navigate, onRefetchDetails]);

  const classes = useStyles();

  return (
    <>
      <Button
        size='small'
        variant='text'
        title={t('consolidatedTransactionDetailsPage.AcceptAndFinalize.Text')}
        {...externalButtonProps}
        onClick={onToggleDealerRateAcceptanceDialog}
        disabled={isAcceptanceBtnDisabled}>
        {externalButtonProps?.title
          ? externalButtonProps?.title
          : t('consolidatedTransactionDetailsPage.AcceptAndFinalize.Text')}
      </Button>
      <GenericDialog
        fullwidth
        isError={false}
        isLoading={false}
        isConfirmationResponsiveDialog={true}
        isDialogOpen={isOpen}
        dialogContentClassName={classes.dialogContentClassName}
        onToggleDialog={onToggleDealerRateAcceptanceDialog}
        stickDrawerTitleToLeft={true}
        disableMobileDrawerHeight={true}
        dialogTitle={''}
        loaderVerticalSpacing='large'
        dialogContent={
          <ClientSideWizardFlowPresenter
            processName={'fundsTransfer'}
            preloadedFormDataConfig={{
              data: {
                initiateAuth: {
                  stepId: 'initiateAuth',
                  values: {
                    onRefetchDetails,
                    transactionDetails,
                    dealerRates,
                    fundTransfersService,
                    nextCallback: onRefetchList,
                    onCancel: onToggleDealerRateAcceptanceDialog,
                    onBackHome: onNavigateBackToDashboard,
                    setCurrentStep,
                  },
                },
                END: {
                  stepId: 'END',
                  values: {
                    onRefetchDetails,
                    transactionDetails,
                    dealerRates,
                    fundTransfersService,
                    nextCallback: onRefetchList,
                    onCancel: onToggleDealerRateAcceptanceDialog,
                    onBackHome: onNavigateBackToDashboard,
                    setCurrentStep,
                  },
                },
              },
              currentStep: currentStep === 'initTransfer' ? 'initTransfer' : 'makeTransfer',
            }}
            stepsConfig={{
              initiateAuth: {
                stepId: 'initiateAuth',
                isFirstStep: currentStep === 'initTransfer',
                cancelHidden: false,
                hasStepIndicator: false,
                Component: InitiateRequestDealerInApp2FA,
                parameters: {},
              },
              END: {
                stepId: 'END',
                isFirstStep: currentStep !== 'initTransfer',
                cancelHidden: false,
                hasStepIndicator: false,
                Component: SuccessAcceptedDealerRate,
                parameters: {},
              },
            }}
            isTakeover={false}
            shouldEnablePaddingBottom={false}
          />
        }
        isSuccess={false}
      />
    </>
  );
};

export { AcceptAndFinalize };
